import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import HomeLayout from '../../components/home_page/HomeLayout';
import Form from '../../components/Form/Form';
import MasterListFooter from '../../components/Footer/Master_Footer/MasterListFooter';
import NavFooterLayout from '../../components/NavNameOfComponent/NavFooterLayout';
import { useDropdownKeyboardNavigation } from '../../components/Custom_Hooks/useDropdownKeyboardNavigation';
import { useDropdownScroll } from '../../components/Custom_Hooks/useDropdownScroll';
import UserAndPasswordCreate from './UserAndPasswordCreate';
import DeletConfirm from '../../components/Modal/DeletConfirm';
import { useDelet } from '../../components/Custom_Hooks/useDelete';
import { prepareFormData } from '../../components/Custom_Hooks/useFormHandler';
import { companyUserDelete, companyUserList } from '../../redux/actions/action-creator';
import { usePermission } from '../../hooks/usePermission';

const initialUserDataList = [];

function UserAndPassword() {
    const dispatch = useDispatch();
    const { userData = null } = useSelector(state => state.auth);
    const [loading, setLoading] = useState(true);
    const [all_data, setAll_data] = useState([]);
    const [enabled, setEnabled] = useState(true);
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const dropdownRef = useRef(null);

    const { add, alter, view } = usePermission('Masters', 'User Management');

    const [data, setData] = useState({
        id: '',
        company_id: ''
    });

    const fetchUsersList = () => {
        const companyId = userData?.company_id || localStorage.getItem("company_id");
        if (companyId) {
            setLoading(true);
            const formData = new FormData();
            formData.append('company_id', companyId);
            dispatch(companyUserList(formData))
                .then((response) => {
                    if (response?.status && response?.data) {
                        const mappedData = response.data.map(item => ({
                            ...item,
                            name: item.ledger_name || item.name || '',
                            account_no: item.ledger_id || item.account_no || '',
                            status: item.user_status || item.status || 'Active'
                        }));
                        setAll_data(mappedData);
                    } else {
                        setAll_data([]);
                    }
                    setLoading(false);
                })
                .catch((err) => {
                    console.error("Error fetching users list:", err);
                    setAll_data([]);
                    setLoading(false);
                });
        } else {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsersList();
    }, [dispatch, userData]);

    const [isView, setIsView] = useState(false);

    const handleOpenAlter = (item) => {
        setSelectedUser(item);
        setIsView(false);
        setIsCreateOpen(true);
    };

    const handleOpenView = (item) => {
        setSelectedUser(item);
        setIsView(true);
        setIsCreateOpen(true);
    };

    // Handle Alt+C or C shortcut key from the table list
    const { selectedItemIndex, setSelectedItemIndex } = useDropdownKeyboardNavigation({
        items: all_data,
        state: !isCreateOpen && !isDeleteOpen,
        initialIndex: 0,
        enableAlphaJump: true,
        getLabel: (item) => item?.name || '',
        onItemAltCSelect: () => {
            if (add) {
                setSelectedUser(null);
                setIsView(false);
                setIsCreateOpen(true);
            }
        },
        onItemSelect: (item) => {
            if (view) handleOpenView(item);
        },
        onItemControlEnterSelect: (item) => {
            if (alter) handleOpenAlter(item);
        },
        onItemAltDSelect: (item) => {
            if (alter) setIsDeleteOpen(true);
        }
    });

    useDropdownScroll({ dropdownRef, selectedItemIndex });

    useEffect(() => {
        if (all_data[selectedItemIndex]) {
            setData({
                id: all_data[selectedItemIndex]?.id || '',
                company_id: userData?.company_id || localStorage.getItem("company_id") || ''
            });
        }
    }, [selectedItemIndex, all_data, userData]);

    const { delteItem, loadingDelete } = useDelet({
        prepareFormData: () => prepareFormData(data),
        submitForm: async (formData) => {
            const res = await dispatch(companyUserDelete(formData));
            if (res?.status) {
                const deletedUsername = all_data[selectedItemIndex]?.username;
                setAll_data(prev => prev.filter(user => user.username !== deletedUsername));
            }
            return res;
        },
        shortcuts: [
            {
                key: 'a',
                ctrlKey: true,
                action: () => delteItem(new Event('submit')),
            },
        ],
        closeModal: () => setIsDeleteOpen(false),
        state: isDeleteOpen
    });

    // Handle successful creation or alteration of user
    const handleUserCreated = (newUserData) => {
        fetchUsersList();
    };

    // Custom layout click handler for mouse users clicking in the footer
    useEffect(() => {
        const handleFooterClick = (e) => {
            const buttonElement = e.target.closest('button');
            if (buttonElement) {
                if (buttonElement.textContent.includes('Create Master') && !isCreateOpen && add) {
                    setSelectedUser(null);
                    setIsView(false);
                    setIsCreateOpen(true);
                } else if (buttonElement.textContent.includes('Delete') && !isCreateOpen && !isDeleteOpen && alter) {
                    setIsDeleteOpen(true);
                } else if (buttonElement.textContent.includes('Alter') && !isCreateOpen && alter) {
                    if (all_data[selectedItemIndex]) {
                        handleOpenAlter(all_data[selectedItemIndex]);
                    }
                }
            }
        };
        document.addEventListener('click', handleFooterClick);
        return () => document.removeEventListener('click', handleFooterClick);
    }, [isCreateOpen, isDeleteOpen, all_data, selectedItemIndex]);

    if (!view) {
        return (
            <HomeLayout>
                <div className="d-flex justify-content-center align-items-center h-100">
                    <h3>You do not have permission to view this page.</h3>
                </div>
            </HomeLayout>
        );
    }

    return (
        <HomeLayout>
            {isCreateOpen && (
                <div style={{ zIndex: 10000 }}>
                    <UserAndPasswordCreate
                        closeModal_sec={() => {
                            setIsCreateOpen(false);
                            setSelectedUser(null);
                            setIsView(false);
                        }}
                        onSuccess={handleUserCreated}
                        editData={selectedUser}
                        viewOnly={isView}
                    />
                </div>
            )}
            <Form
                title='User Management'
                col='col-12'
                loading={loading}
                Footer={MasterListFooter}
                createMasterEnabled={add}
                deleteEnabled={alter}
            >
                {isDeleteOpen && (
                    <div style={{ zIndex: 10000 }}>
                        <DeletConfirm
                            handleSubmit={delteItem}
                            loading={loadingDelete}
                            closeModal={() => setIsDeleteOpen(false)}
                        />
                    </div>
                )}
                <div className="row p-0 m-0 border_2_1px_top border_2_2px_bottom mt-0">
                    <div
                        className="col-1 font_size p-0 m-0 px-1 text-end fw-bold"
                        style={{ width: "5%" }}
                    >
                        Sr No.
                    </div>
                    <div
                        className="col-3 font_size p-0 m-0 px-1 text-start fw-bold"
                        style={{ width: "22%" }}
                    >
                        User
                    </div>
                    <div
                        className="col-1 font_size m-0 p-0 px-1 text-end fw-bold"
                        style={{ width: "10%" }}
                    >
                        Ac No.
                    </div>
                    <div
                        className="col-2 font_size m-0 p-0 px-1 text-start fw-bold"
                        style={{ width: "12%" }}
                    >
                        Username
                    </div>
                    <div
                        className="col-2 font_size m-0 p-0 px-1 text-start fw-bold"
                        style={{ width: "12%" }}
                    >
                        Designation
                    </div>
                    <div
                        className="col-1 font_size m-0 p-0 px-1 text-end fw-bold"
                        style={{ width: "10%" }}
                    >
                        Mobile
                    </div>
                    <div
                        className="col-2 font_size m-0 p-0 px-1 text-start fw-bold"
                        style={{ width: "21%" }}
                    >
                        e-Mail
                    </div>
                    <div
                        className="col-1 font_size p-0 m-0 px-1 text-start fw-bold"
                        style={{ width: "8%" }}
                    >
                        User Status
                    </div>
                </div>
                <div className='row gx-0 p-0 mb-2' style={{ overflowY: 'hidden' }}>
                    <div className='col m-0 p-0' ref={dropdownRef} style={{ overflowY: 'auto', height: '75.3vh' }}>
                        {
                            all_data.map((item, index) => {
                                return (
                                    <div
                                        key={index}
                                        onClick={() => setSelectedItemIndex(index)}
                                        onDoubleClick={() => handleOpenAlter(item)}
                                        style={{ cursor: "pointer" }}
                                        className={`row gx-0 p-0 m-0 mt-0 ${index === selectedItemIndex ? "selected" : ""}`}
                                    >
                                        <div
                                            className="col-1 font_size p-0 m-0 px-1 text-end"
                                            style={{ width: "5%" }}
                                        >
                                            {index + 1}
                                        </div>
                                        <div
                                            className="col-3 font_size p-0 m-0 px-1 text-start"
                                            style={{ width: "22%" }}
                                        >
                                            {item?.name}
                                        </div>
                                        <div
                                            className="col-1 font_size m-0 p-0 px-1 text-end"
                                            style={{ width: "10%" }}
                                        >
                                            {item?.account_no}
                                        </div>
                                        <div
                                            className="col-2 font_size m-0 p-0 px-1 text-start"
                                            style={{ width: "12%" }}
                                        >
                                            {item?.username}
                                        </div>
                                        <div
                                            className="col-2 font_size m-0 p-0 px-1 text-start"
                                            style={{ width: "12%" }}
                                        >
                                            {item?.designation}
                                        </div>
                                        <div
                                            className="col-1 font_size m-0 p-0 px-1 text-end"
                                            style={{ width: "10%" }}
                                        >
                                            {item?.mobile_no}
                                        </div>
                                        <div
                                            className="col-2 font_size m-0 p-0 px-1 text-start"
                                            style={{ width: "21%" }}
                                        >
                                            {item?.email}
                                        </div>
                                        <div
                                            className="col-1 font_size p-0 m-0 px-1 text-start fw-bold"
                                            style={{
                                                width: "8%",
                                                color: item?.status === 'Active' ? '#198754' : '#dc3545'
                                            }}
                                        >
                                            {item?.status}
                                        </div>
                                    </div>
                                )
                            })
                        }
                    </div>
                </div>
                <NavFooterLayout>
                    <b className='ps-2'> {all_data.length} User(s)</b>
                </NavFooterLayout>
            </Form>
        </HomeLayout>
    )
}

export default UserAndPassword;